import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { NO_LOCATION_KEY } from 'constants/general';
import { compareCountryGroupKeys } from 'utils/country';

import ScenarioShowcaseDeleteModal from './components/ScenarioShowcaseDeleteModal/ScenarioShowcaseDeleteModal';
import ScenarioShowcaseEntities from './components/ScenarioShowcaseEntities/ScenarioShowcaseEntities';
import ScenarioShowcaseHeader from './components/ScenarioShowcaseHeader/ScenarioShowcaseHeader';
import ScenarioShowcaseInfo from './components/ScenarioShowcaseInfo/ScenarioShowcaseInfo';

import type IScenarioEntity from 'components/AddScenarioSidebar/interfaces/IScenarioEntity';
import type IScenarioShowcaseInfoRow from './components/ScenarioShowcaseInfo/interfaces/IScenarioShowcaseInfoRow';
import type IScenarioShowcase from './interfaces/IScenarioShowcase';
import type IScenarioShowcaseCountryGroup from './interfaces/IScenarioShowcaseCountryGroup';
import type IScenarioShowcaseEntity from './interfaces/IScenarioShowcaseEntity';

import styles from './sass/ScenarioShowcase.module.scss';

function ScenarioShowcase({
  scenario,
  boardEntities,
  isActive,
  isViewActive = false,
  isViewEditDisabled = false,
  headerStartContent,
  hideActions = false,
  showEmptyInfoRows = false,
  stopEntitiesTogglePropagation = false,
  onSelect,
  onView,
  onEdit,
  onDeleteClick,
  onDelete,
}: IScenarioShowcase): React.JSX.Element {
  const { t } = useTranslation();
  const [isEntitiesExpanded, setIsEntitiesExpanded] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const entityMap = useMemo(() => {
    const map = new Map<number, IScenarioEntity>();
    boardEntities.forEach((e) => map.set(e.id, e));
    return map;
  }, [boardEntities]);

  const groups = useMemo<IScenarioShowcaseCountryGroup[]>(() => {
    const groupedUnits = new Map<string, IScenarioShowcaseEntity[]>();

    scenario.units.forEach((unit) => {
      const entity = entityMap.get(unit.id);
      const displayEntity: IScenarioShowcaseEntity = {
        id: unit.id,
        name: entity?.name || unit.name,
        type: entity?.type || unit.type,
        isMissing: !entityMap.has(unit.id),
      };
      const countryKey = entity?.country || NO_LOCATION_KEY;

      if (!groupedUnits.has(countryKey)) {
        groupedUnits.set(countryKey, []);
      }

      groupedUnits.get(countryKey)?.push(displayEntity);
    });

    return Array.from(groupedUnits.entries())
      .sort(([leftCountryKey], [rightCountryKey]) => (
        compareCountryGroupKeys(leftCountryKey, rightCountryKey)
      ))
      .map(([countryKey, units]) => ({
        countryKey,
        units,
      }));
  }, [entityMap, scenario.units]);

  const geography = useMemo(
    () => groups
      .filter(({ countryKey }) => countryKey !== NO_LOCATION_KEY)
      .map(({ countryKey }) => countryKey),
    [groups],
  );

  const formatPeriod = (): string | null => {
    if (!scenario.affectedPeriod || !scenario.affectedPeriodUnit) return null;

    const count = scenario.affectedPeriod;
    const unit = scenario.affectedPeriodUnit;

    return `${count} ${unit}${count !== 1 ? 's' : ''}`;
  };

  const formatImpact = (): string | null => {
    if (scenario.productivityImpact === undefined || scenario.productivityImpact === null) return null;

    const value = scenario.productivityImpact;

    return `${value > 0 ? '+' : ''}${value}%`;
  };

  const handleOpenDeleteModal = () => {
    onDeleteClick?.();
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    if (isDeleting) return;

    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (isDeleting) return;
    if (!onDelete) return;

    try {
      setIsDeleting(true);
      await onDelete();
      setIsDeleteModalOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleEntities = () => {
    setIsEntitiesExpanded((prevState) => !prevState);
  };

  const handleKeyDownCard = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onSelect();
  };

  const period = formatPeriod();
  const impact = formatImpact();
  const infoRows = useMemo<IScenarioShowcaseInfoRow[]>(() => {
    const rows: IScenarioShowcaseInfoRow[] = [];

    if (period || showEmptyInfoRows) {
      rows.push({
        label: t('valueChainMap.affectedPeriodLabel'),
        value: period || '-',
      });
    }

    if (impact || showEmptyInfoRows) {
      rows.push({
        label: t('valueChainMap.scenarioProductivityImpact'),
        value: impact || '-',
      });
    }

    if (geography.length > 0 || showEmptyInfoRows) {
      rows.push({
        label: t('valueChainMap.affectedGeography'),
        value: geography.length > 0 ? geography.join(', ') : '-',
      });
    }

    return rows;
  }, [geography, impact, period, showEmptyInfoRows, t]);
  const isViewDisabled = !isActive || isViewEditDisabled;

  return (
    <div
      className={clsx(styles.card, { [styles.cardActive]: isActive })}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDownCard}
      aria-pressed={isActive}
    >
      <ScenarioShowcaseHeader
        title={scenario.name}
        isViewActive={isViewActive}
        isViewDisabled={isViewDisabled}
        isViewEditDisabled={isViewEditDisabled}
        startContent={headerStartContent}
        hideActions={hideActions}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete ? handleOpenDeleteModal : undefined}
      />
      <ScenarioShowcaseInfo rows={infoRows} />
      <ScenarioShowcaseEntities
        totalUnits={scenario.units.length}
        isExpanded={isEntitiesExpanded}
        groups={groups}
        onToggle={handleToggleEntities}
        stopTogglePropagation={stopEntitiesTogglePropagation}
      />
      <ScenarioShowcaseDeleteModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleting}
        scenarioName={scenario.name}
        onReject={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

export default ScenarioShowcase;
